import { BestPracticePostDownloadDto, CollaborationServiceSetup, ContentPostDownloadDto, ControllerHelper, EventController, EventUploadDto, FileEntryDownloadDto, PodcastPostDownloadDto, PorscheMomentPostDownloadDto, QuizDownloadDto, SlideShowDownloadDto, TubePostDownloadDto, WikiArticleDownloadDto } from 'collaboration-service';
import DateTimeFns from 'components/DateTime/DateTimeFns';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { Button, ButtonFloates, ButtonKind, Dimmer, getLinkByName, ImgIcons, RatingView } from 'imaginarity-react-ui';
import { FloatedLeft } from 'layout/masterLayouts/MasterLayoutsSC';
import _ from 'lodash';
import * as React from 'react';
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { bannerTop } from 'services/Config';
import { shallowCompare } from 'services/Helpers';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { sanitizedNothingAllowedAttributes } from 'services/Helpers/SanitizedHelper';
import { getCMSContent, getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import SlideShowContent from 'views/SlideShow/SlideShowContent';
import { PlaylistDetailSC as T } from "../../PlaylistDetailSC";
import MarkAsReadButton from './MarkAsReadButton';
import { PlaylistDimmerProps } from './PlaylistDimmer';
import PreviewBestPractice from './Previews/PreviewBestPractice';
import PreviewContentPost from './Previews/PreviewContentPost';
import PreviewFileEntryVideoPodcast from './Previews/PreviewFileEntryVideoPodcast';
import PreviewMoment from './Previews/PreviewMoment';
import PreviewQuiz from './Previews/PreviewQuiz';
import PreviewTubePodcast from './Previews/PreviewTubePodcast';
import PreviewWikiArticle from './Previews/PreviewWikiArticle';
import ScormContainer from 'services/Scorm/ScormContainer';

const mapper = (s: ApplicationState) => ({
    contentLanguage: s.contentLanguage,
    currentGroup: s.currentGroup,
    currentArticle: s.wikiState.currentArticle
});

interface PlaylistUniversalDimmerProps extends PlaylistDimmerProps<WikiArticleDownloadDto | TubePostDownloadDto | PodcastPostDownloadDto | ContentPostDownloadDto | PorscheMomentPostDownloadDto | BestPracticePostDownloadDto | QuizDownloadDto | FileEntryDownloadDto | SlideShowDownloadDto> {
}

const PlaylistUniversalDimmer = (p: PlaylistUniversalDimmerProps) => {
    const { contentLanguage, currentGroup, currentArticle } = useAppSelector(mapper, shallowCompare);
    const { id, item, itemData, result, playlistId, state,
        isOptional, transition, elementIsReference, nodeIsDone, warning, closeDimmer, loadPlaylistStates, playlistStateId,
        node } = p;
    const { t, currentLanguage } = useImgI18N('playlist');
    const language = contentLanguage ?? currentLanguage;
    const type = item.type;
    const dataType = itemData.type;
    const isType = (t: string) => type === t;
    const isDataType = (t: string) => dataType === t;
    const [downloaded, setDownloaded] = React.useState<boolean>(false);

    const isWiki = isType("WikiArticle");
    const isTube = isType("TubePost");
    const isPodcast = isType("PodcastPost");
    const isContentPost = isType("ContentPost");
    const isPmPost = isType("PorscheMomentPost");
    const isBpPost = isType("BestPracticePost");
    const isQuiz = isType("Quiz");
    const isFileEntry = isType("FileEntry");
    const isSlideShow = isType("SlideShow");
    const isUrlFile = isDataType("url-file");
    const isVideoFile = isDataType("video");
    const isAudioFile = isDataType("audio");
    const isPdfFile = isDataType("pdf");
    const isImageFile = isDataType("image");
    const isDownloadFile = isDataType("download");
    const isDocumentFile = isDataType("document");
    const isScormFile = isDataType("wbt");


    const translatedContent = React.useMemo(() => {
        if (!isFileEntry) return null;

        const translatedContent = getCMSContent({ fileEntry: item as FileEntryDownloadDto, filePosts: {}, language });
        return translatedContent;
    }, [item, language, isFileEntry]);

    const download = React.useMemo(() => () => {
        if (playlistId) {
            const ev: EventUploadDto = {
                eventType: "PostViewed",
                group_id: item.group_id,
                payload: {
                    "PostId": item.id,
                    "From": "playlist",
                    "PlaylistId": playlistId,
                }
            };
            ControllerHelper.singleCall({ ev }, EventController.PostEvent);
            const ev1: EventUploadDto = {
                eventType: "Finished",
                group_id: item.group_id,
                payload: {
                    "PostId": item.id,
                    "From": "playlist",
                    "PlaylistId": playlistId,
                }
            };
            ControllerHelper.singleCall({ ev: ev1 }, EventController.PostEvent);
        }
        const dwn = getLinkByName(translatedContent?.media, "download")?.uri ?? "";
        const ele = document.createElement("a");
        ele.setAttribute("download", "download");
        ele.setAttribute("href", dwn ?? "");
        ele.style.display = "none";
        ele.click();
        setDownloaded(true);
        if (loadPlaylistStates)
            loadPlaylistStates(item.id);
    }, [translatedContent, item.id, playlistId, item.group_id, loadPlaylistStates]);


    const content = isFileEntry ? getCMSContent({ fileEntry: item as FileEntryDownloadDto, filePosts: {}, language }) : undefined;
    const link = (content ? _.find(content?.media?.links, l => l.ref === "external-link")?.uri ?? "" : undefined)
        ?.replace("{{token}}", CollaborationServiceSetup.client.token)
        ?.replace("{{playlistId}}", playlistId)
        ?.replace("{{nodeId}}", node?.id ?? "")
        ?.replace("{{playlistStateId}}", playlistStateId ?? "");

    const renderTitleText = () => {


        if (isWiki || isTube || isPodcast || isContentPost || isPmPost || isBpPost || isSlideShow) {
            return (
                <SafeHTML html={getTranslated(item.headlines, language)?.text ?? ""} allowedTags={[]} allowedAttributes={{}} />
            );
        }
        else if (isFileEntry) {
            if (isUrlFile) {
                const title = itemData.title ?? getTranslated(item?.headlines, language)?.text ?? link;
                return <SafeHTML html={title} allowedTags={[]} allowedAttributes={{}} />
            }
            else {
                const title = itemData.title ?? getTranslated(item?.headlines, language)?.text ?? content?.media?.fileName;
                return <SafeHTML html={title} allowedTags={[]} allowedAttributes={{}} />
            }


        }
        return null;
    };

    const renderReadButton = () => {
        if (isUrlFile) {
            return (<Button
                icon="link external"
                content={t("open link")}
                bordered
                kind="secondary"
                floated="right"
                asHtmlLinkToRoute={link}
                asHtmlLinkToRouteTarget='_blank'
                onClick={clickOnItem}
                tooltip={{
                    tooltipText: t("link opens in new browser tab"),
                    zIndex: 2147483638
                }}
            />);
        }
        else if (isDocumentFile || isDownloadFile) {
            return (<Button
                icon="link external"
                content={state.state === "Finished" ? t("start download again") : t("start download")}
                bordered
                kind="secondary"
                floated="right"
                onClick={download}
            />);
        }


        else if (!itemData.link) return <div />;

        else {
            const typeToLabelMap: Record<string, string> = {
                WikiArticle: t("open in ppe"),
                TubePost: t("open in tube"),
                PodcastPost: t("open in podcast"),
                ContentPost: t("open in smw"),
                PorscheMomentPost: t("open in moments"),
                BestPracticePost: t("open in pri"),
                FileEntryUri: t("open link"),
            };
            const content = typeToLabelMap[item.type ?? ""] as string | undefined;
            if (!content) return null;

            const commonProps = {
                icon: "link external" as ImgIcons,
                bordered: true,
                kind: "secondary" as ButtonKind,
                floated: "right" as ButtonFloates,
                asHtmlLinkToRoute: itemData.link,
                onClick: clickOnItem,
            };

            return <Button
                {...commonProps}
                content={content}
                {...((isWiki || isUrlFile) && { asHtmlLinkToRouteTarget: "_blank", tooltip: { tooltipText: t("link opens in new browser tab"), zIndex: 2147483638 } })}
            />;
        }

    };


    const renderImageHeader = () => {
        if (isWiki) {
            return <T.DimmerImageContainer
                imageUrl={(currentArticle ? getMediaLink(currentArticle, a => a.media) : undefined) ?? bannerTop.wikiArticleAlternative}
                style={{ zIndex: 1 }}
            />;
        }
        else if (isTube || isPodcast) {
            return <PreviewTubePodcast
                playlistId={playlistId}
                playlistStateId={playlistStateId}
                nodeId={node?.id ?? ""}
                id={id}
                isPodcast={isPodcast}
                loadPlaylistStates={loadPlaylistStates}
            />;
        }
        else if (isContentPost) {
            return (<PreviewContentPost item={item as ContentPostDownloadDto} playlistStateId={playlistStateId} />);
        }
        else if (isPmPost) {
            return (<PreviewMoment id={id} context={p.playlistStateId} />);
        }
        else if (isBpPost) {
            return (<PreviewBestPractice item={item as BestPracticePostDownloadDto} isTitle id={id} />);
        }
        else if (isSlideShow) {
            return (<T.SlideShowContainer hasToken={true}>
                <SlideShowContent
                    slideShow={item as SlideShowDownloadDto}
                    lng={language}
                    slideWidth={880}
                />
            </T.SlideShowContainer>);
        }
        else if (isQuiz) {
            return (
                <PreviewQuiz
                    item={item as QuizDownloadDto}
                    id={id}
                    closeDimmer={closeDimmer}
                    playlistStateId={playlistStateId}
                />
            );
        }
        else if (isFileEntry) {
            if (isUrlFile) {
                const imageUrl = itemData.image;
                return (<T.DimmerImageContainer imageUrl={imageUrl} lightBG style={{ backgroundSize: "contain", aspectRatio: "16 / 9", marginTop: -1 }} />);
            }
            else if (isVideoFile) {
                return <PreviewFileEntryVideoPodcast
                    playlistStateId={playlistStateId}
                    id={id}
                    isPodcast={false}
                    nodeId={node?.id ?? ""}
                    playlistId={playlistId}
                    loadPlaylistStates={loadPlaylistStates}
                />
            }
            else if (isAudioFile) {
                return <PreviewFileEntryVideoPodcast
                    playlistStateId={playlistStateId}
                    id={id}
                    isPodcast={true}
                    nodeId={node?.id ?? ""}
                    playlistId={playlistId}
                    loadPlaylistStates={loadPlaylistStates}
                />
            }
            else if (isPdfFile) {
                return <embed src={getLinkByName(translatedContent?.media, "self")?.uri ?? ""} style={{ width: "100%", height: "100%" }} />
            }
            else if (isImageFile) {
                return <img src={getLinkByName(translatedContent?.media, "self")?.uri ?? ""} alt="Scrollable content" style={{ width: "100%", height: "100%", objectFit: "contain", }} />
            }
            else if (isDocumentFile || isDownloadFile) {
                return <div style={{ padding: "30px 40px", fontSize: "1rem", background: "yellow", fontWeight: "bolder", color: "red" }}>
                    {downloaded ? t('states will be actualized, do not wait for it') : t("this content can only be downloaded.")}
                </div>
            }
            else if (isScormFile) {
                const ct = getCMSContent({ fileEntry: item as FileEntryDownloadDto, filePosts: {}, language });
                const scorm = ct.media?.thumbnails?.find(t => t.mediaType === "decompressed-zip");
                return (
                    <ScormContainer
                        id={scorm?.reference ?? ""}
                        height="100%"
                        playlistStateId={playlistStateId}
                        metaData={`${item.id}|${node?.id}`}
                    />
                )
            }
            else return null;
        }
        return null;
    };
    const renderDescription = () => {
        if (isWiki) {
            return (<PreviewWikiArticle directId={id} context={playlistStateId} />);
        }
        else if (isTube || isPodcast || isContentPost || isUrlFile || isVideoFile || isPdfFile || isImageFile || isDownloadFile || isDocumentFile || isSlideShow) {
            return (
                <SafeHTML
                    html={getTranslated(item.descriptions, language).text ?? ""}
                    allowedTags={["strong", "em", "b", "i", "u"]}
                    allowedAttributes={sanitizedNothingAllowedAttributes}
                />
            );
        }
        else if (isBpPost) {
            return (<PreviewBestPractice item={item as BestPracticePostDownloadDto} id={id} />);
        }

        return null;
    };

    const postViewed = React.useCallback(() => {
        if (!currentGroup || !playlistId) return;
        const ev: EventUploadDto = {
            eventType: "PostViewed",
            group_id: currentGroup.id,
            payload: {
                PostId: item.id,
                From: "playlist",
                PlaylistId: playlistId,
            },
        };
        ControllerHelper.singleCall({ ev }, EventController.PostEvent);
    }, [currentGroup, playlistId, item.id]);

    React.useEffect(() => {
        postViewed();
    }, [postViewed]);

    const clickOnItem = postViewed;

    return (
        <Dimmer active >
            <T.UniversalDimmerContainer
                isPmPost={isPmPost}
                isQuiz={isQuiz}
                style={{ maxWidth: (isPdfFile || isScormFile) ? "90vw" : 880 }}
            >
                <T.RenderImageHeaderContainer style={{ height: (isScormFile || isPdfFile) ? "calc(90vh - 200px)" : "fit-content" }}>
                    {renderImageHeader()}
                </T.RenderImageHeaderContainer>

                {!isQuiz &&
                    <>
                        <T.UniversalDimmerInfoContainer>
                            <T.DimmerInfoContainerTitle>
                                {renderTitleText()}
                            </T.DimmerInfoContainerTitle>
                            <T.DimmerInfoContainerButton>
                                {item.rating > 0 ?
                                    <T.DimmerInfoContainerRating>
                                        <RatingView
                                            rating={item.rating}
                                            maxStars={5}
                                            size={24}
                                            filledOut={true}
                                        />

                                    </T.DimmerInfoContainerRating>
                                    :
                                    <div />
                                }
                                {renderReadButton()}
                                {(!isDocumentFile && !isDownloadFile) && state.state !== "Finished" &&
                                    <MarkAsReadButton
                                        playlistId={playlistId}
                                        playlistItemState={state}
                                        item={item}
                                        lastAction={loadPlaylistStates}
                                    />
                                }
                                <Button
                                    floated='right'
                                    icon='times'
                                    onClick={closeDimmer}
                                    kind='secondary'
                                    tooltip={{ tooltipText: t("close"), position: "top", zIndex: 2147483639 }}
                                />
                            </T.DimmerInfoContainerButton>

                            <T.DimmerInfoContainerDate>
                                {result.dueToDate &&
                                    <FloatedLeft>
                                        <DateTimeFns
                                            date={result.dueToDate}
                                            noTime
                                            hideIcons
                                            textColor="grey"
                                        />
                                    </FloatedLeft>
                                }
                                {isOptional ?
                                    <T.ColoredSpan color={state.state === "Finished" ? "@accentGreen" : "inherit"}>
                                        {state.state === "Finished" ? t("done") : t("optional")}
                                    </T.ColoredSpan>
                                    :
                                    (transition === "ReferenceDone" || transition === "ReferenceDoneWithValue") ?
                                        elementIsReference ?
                                            <T.ColoredSpan color={(result.text === "100%" || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : result.color}>
                                                {nodeIsDone ? t("done") : warning ? t("warning") : result.text}
                                            </T.ColoredSpan>
                                            :
                                            <T.ColoredSpan color={result.text === "100%" ? "@accentGreen" : "@middleLightGrey"}>
                                                {result.text === "100%" ? t("done") : t("optional")}
                                            </T.ColoredSpan>
                                        :
                                        <T.ColoredSpan color={result.text === "100%" ? "@accentGreen" : warning ? "@warning" : result.color}>
                                            {result.text === "100%" ? t("done") : t(result.text)}
                                        </T.ColoredSpan>
                                }
                            </T.DimmerInfoContainerDate>
                        </T.UniversalDimmerInfoContainer>
                        <T.RenderDescriptionContainer isPdf={isPdfFile} isWiki={isWiki} style={{ height: isSlideShow ? "fit-content" : undefined }}>
                            {renderDescription()}
                        </T.RenderDescriptionContainer>
                    </>
                }
            </T.UniversalDimmerContainer>
        </Dimmer>
    );
}
export default PlaylistUniversalDimmer;